import { CloudUploadOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Card, Input, message, Select, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import * as Component from 'components';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  VietnamUploadItem,
  VietnamUploadListParams,
  VietnamUploadScope,
  VietnamUploadStatus,
} from 'service/api/vietnamUploadService';
import { useIngestVietnamUpload, useVietnamUploads } from 'hooks/useVietnamUploads';
import { accountStore } from 'store/accountStore';

const { Title, Text } = Typography;

const STATUS_META: Record<VietnamUploadStatus, { color: string; label: string }> = {
  uploaded: { color: 'blue', label: 'Uploaded' },
  ingesting: { color: 'gold', label: 'Ingesting' },
  ingested: { color: 'green', label: 'Ingested' },
  failed: { color: 'red', label: 'Failed' },
};

const formatFileSize = (bytes: number | null): string => {
  if (!bytes || bytes <= 0) return '-';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${units[i]}`;
};

const DEFAULT_PAGE_SIZE = 20;

export interface VietnamUploadsListProps {
  /**
   * 'own'(기본) = 자(CP) 본인 회사 업로드. `vietnam-uploads:read`.
   * 'all' = 모(CP)/admin 전 회사 업로드. `vietnam-uploads-admin:read`.
   */
  scope?: VietnamUploadScope;
}

// Vietnam 업로드 목록 + Ingest(→ Video 에셋 + 트랜스코딩).
// 회사 스코프 격리는 서버가 request.user.company / 권한 기준으로 강제한다.
// 프론트의 scope는 요청 의도일 뿐, 접근 판정은 라우터 가드 + 서버가 담당한다.
const VietnamUploadsList: React.FC<VietnamUploadsListProps> = ({ scope = 'own' }) => {
  const navigate = useNavigate();
  const isAdminScope = scope === 'all';
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [statusFilter, setStatusFilter] = useState<VietnamUploadStatus | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [company, setCompany] = useState('');

  const params: VietnamUploadListParams = {
    page,
    page_size: pageSize,
    status: statusFilter,
    search: search || undefined,
    scope: isAdminScope ? 'all' : undefined,
    company: isAdminScope ? company || undefined : undefined,
  };

  const { data, isFetching, refetch } = useVietnamUploads(params);
  const ingest = useIngestVietnamUpload();

  const handleIngest = (record: VietnamUploadItem) => {
    ingest.mutate(record.id, {
      onSuccess: response => {
        const job = response.batch_jobs?.[0];
        if (job?.video_id) {
          message.success(`Asset #${job.video_id} created. Transcoding started.`);
        } else if (response.failures?.length) {
          message.error(`Ingest failed: ${response.failures[0] ?? 'Unknown error'}`);
        } else {
          message.success('Ingest started.');
        }
      },
      onError: (error: unknown) => {
        const response = (error as { response?: { status?: number; data?: any } })?.response;
        const status = response?.status;
        const detail = response?.data?.detail || response?.data?.error || (error as Error)?.message;
        if (status === 400) {
          message.error(detail || 'Already ingested or invalid key.');
        } else if (status === 404) {
          message.error('You do not have access to this upload.');
        } else if (status === 500) {
          message.error('Ingest pipeline failed. Please retry.');
        } else {
          message.error(`Ingest failed: ${detail ?? 'Unknown error'}`);
        }
      },
    });
  };

  const columns: ColumnsType<VietnamUploadItem> = [
    {
      title: 'File Name',
      dataIndex: 'file_name',
      key: 'file_name',
      ellipsis: true,
      render: (value: string, record) => <span title={record.key}>{value}</span>,
    },
    // 소유 회사는 전 회사 조회일 때만 (자 CP 화면에서는 항상 본인 회사라 무의미).
    ...(isAdminScope
      ? ([
          {
            title: 'Company',
            dataIndex: 'company_slug',
            key: 'company_slug',
            width: 160,
            ellipsis: true,
            render: (value: string) => value || '-',
          },
        ] as ColumnsType<VietnamUploadItem>)
      : []),
    // 업로더는 두 화면 모두 노출한다 — 같은 회사 안에서도 계정이 여럿이라 누가 올렸는지 구분이 필요하다.
    {
      title: 'Uploaded By',
      dataIndex: 'uploaded_by',
      key: 'uploaded_by',
      width: 200,
      ellipsis: true,
      render: (value: string | null) => value || '-',
    },
    {
      title: 'Size',
      dataIndex: 'file_size',
      key: 'file_size',
      width: 110,
      render: (value: number | null) => formatFileSize(value),
    },
    {
      title: 'Uploaded',
      dataIndex: 'created',
      key: 'created',
      width: 180,
      render: (value: string) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: VietnamUploadStatus) => {
        const meta = STATUS_META[status] ?? { color: 'default', label: status };
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    // Action(Ingest)은 모(CP)/admin 화면 전용이다.
    // 자(CP)의 My Uploads는 올린 결과를 Status로 확인만 하고, ingest 실행은 모 CP가 맡는다.
    ...(isAdminScope
      ? ([
          {
            title: 'Action',
            key: 'action',
            width: 200,
            render: (_, record) => {
              if (record.status === 'ingested') {
                return record.video_id ? (
                  <Button type="link" onClick={() => navigate(`/assets/edit/${record.video_id}`)}>
                    Asset #{record.video_id}
                  </Button>
                ) : (
                  <Text type="secondary">Ingested</Text>
                );
              }
              if (record.status === 'ingesting') {
                return <Text type="secondary">Ingesting…</Text>;
              }
              // uploaded | failed → Ingest 가능 (failed는 재시도)
              const isThisRowIngesting = ingest.isPending && ingest.variables === record.id;
              return (
                <Button
                  type="primary"
                  size="small"
                  icon={<CloudUploadOutlined />}
                  loading={isThisRowIngesting}
                  disabled={ingest.isPending}
                  onClick={() => handleIngest(record)}
                >
                  {record.status === 'failed' ? 'Retry Ingest' : 'Ingest'}
                </Button>
              );
            },
          },
        ] as ColumnsType<VietnamUploadItem>)
      : []),
  ];

  const results = data?.results ?? [];
  const total = data?.count ?? 0;

  return (
    <Component.BasePageComponent>
      <div style={{ padding: '24px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <Title level={3} style={{ margin: 0 }}>
            {isAdminScope ? 'All CP Uploads' : 'My Uploads'}
          </Title>
          <Space>
            {accountStore.canAddVietnamUpload() && (
              <Button icon={<CloudUploadOutlined />} onClick={() => navigate('/assets/add-vn')}>
                Upload
              </Button>
            )}
            <Button icon={<ReloadOutlined />} loading={isFetching} onClick={() => refetch()}>
              Refresh
            </Button>
          </Space>
        </div>

        <Space style={{ marginBottom: '16px' }} wrap>
          <Input.Search
            allowClear
            placeholder="Search file name"
            style={{ width: 260 }}
            onSearch={value => {
              setPage(1);
              setSearch(value);
            }}
          />
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 160 }}
            value={statusFilter}
            onChange={value => {
              setPage(1);
              setStatusFilter(value);
            }}
            options={(Object.keys(STATUS_META) as VietnamUploadStatus[]).map(s => ({
              value: s,
              label: STATUS_META[s].label,
            }))}
          />
          {isAdminScope && (
            <Input.Search
              allowClear
              placeholder="Company slug"
              style={{ width: 200 }}
              onSearch={value => {
                setPage(1);
                setCompany(value);
              }}
            />
          )}
        </Space>

        <Card bodyStyle={{ padding: 0 }}>
          <Table<VietnamUploadItem>
            rowKey="id"
            loading={isFetching}
            columns={columns}
            dataSource={results}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              onChange: (nextPage, nextSize) => {
                setPage(nextPage);
                setPageSize(nextSize);
              },
            }}
          />
        </Card>
      </div>
    </Component.BasePageComponent>
  );
};

export default observer(VietnamUploadsList);
